// #3293 - Equitation / Medal sections, the scribe's local half. Offline-first
// like every other scribe write: a move, reorder or draw
//   1. is planned with score-common's planSubsectionChanges (the SAME function
//      score-api runs, so the compacted section numbers and the renumbered
//      neighbours match what the server will compute),
//   2. is applied to the cached rides at once (the grid never waits on the
//      network), and
//   3. ships as ONE `setRideSubsection` outbox op carrying the whole final
//      state of every affected ride, sent to POST /rides/subsection (a batch
//      is all-or-nothing, and an explicit final state replays idempotently).
//
// The op's entityKey is the dragged rider (the card's modelling on
// requestRideReview). A server 409 (`section_class_locked`: the class was
// already signed) lands as an ordinary Outbox problem row.
//
// overlayPendingSubsections is the race guard, same as reviewRequests.js: a
// revalidation that lands while the op is still queued would put the server's
// older section fields over the optimistic cache rows.
import { planSubsectionChanges } from "score-common";
import { enqueue, peekOutbox, rides } from "./db";

export const SUBSECTION_OP_TYPE = "setRideSubsection";

let opCounter = 0;
export function subsectionOpId(rideId) {
  opCounter += 1;
  return `subsection:${rideId}:${Date.now()}:${opCounter}`;
}

const pick = (r) => ({
  rideId: r.rideId,
  subsection: r.to.subsection,
  subsectionOrder: r.to.subsectionOrder,
});

/**
 * Pure: the writes a set of changes produces against a class's rides.
 * `classRides`: [{ id, rideNumber, subsection, subsectionOrder }].
 * `changes`: planSubsectionChanges' input. Returns the payload rows.
 */
export function planRideSubsections(classRides, changes) {
  return planSubsectionChanges(classRides, changes).writes.map(pick);
}

/**
 * Apply `rows` ({ rideId, subsection, subsectionOrder }) onto the cached ride
 * rows and queue ONE op. Resolves to the op (null when nothing changes).
 */
export async function queueRideSubsections({ primaryRideId, rows }) {
  if (!rows.length) return null;
  const op = await enqueue({
    opId: subsectionOpId(primaryRideId),
    type: SUBSECTION_OP_TYPE,
    entityKey: primaryRideId,
    tempId: null,
    payload: { rides: rows },
  });
  await applyRowsToCache(rows);
  return op;
}

export async function applyRowsToCache(rows) {
  for (const row of rows) {
    const cached = await rides.get(row.rideId);
    if (!cached) continue;
    await rides.put({
      ...cached,
      subsection: row.subsection,
      subsectionOrder: row.subsectionOrder,
    });
  }
}

// Re-stamps the section fields of every still-queued op onto `rows`, in FIFO
// order so a later move wins over an earlier one.
export async function overlayPendingSubsections(rows) {
  if (!rows?.length) return rows;
  const ops = (await peekOutbox()).filter((op) => op.type === SUBSECTION_OP_TYPE);
  if (!ops.length) return rows;
  const latest = new Map();
  for (const op of ops) {
    for (const r of op.payload?.rides ?? []) latest.set(r.rideId, r);
  }
  return rows.map((row) => {
    const hit = latest.get(row.id);
    if (!hit) return row;
    return { ...row, subsection: hit.subsection, subsectionOrder: hit.subsectionOrder };
  });
}
